import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  AccountInfo,
  InteractionStatus,
  SilentRequest
} from '@azure/msal-browser';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { msalConfig, loginRequest, apiRequest, UserRole, hasRequiredRole } from '../config/authConfig';
import { resolveTenantIdFromToken } from '../utils/jwtTenantResolver';

export interface AuthContextState {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  userRoles: string[];
  /**
   * Tenant ID resolved from the JWT access token at login (VITE_TENANT_CLAIM_NAME claim).
   * Null before authentication completes or if E1 is not yet configured.
   * Dev fallback: VITE_CUSTOMER_ID (Vite dev server only).
   */
  tenantId: string | null;
  isLoading: boolean;
  error: string | null;
  hasRole: (role: UserRole) => boolean;
  login: (loginHint?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextState | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract user roles and tenant ID from token claims
  useEffect(() => {
    const extractClaimsFromToken = async () => {
      if (account && isAuthenticated) {
        // Keep the loading gate up while we resolve claims. This prevents a brief
        // "Access Denied" flash during the async token read on a fresh sign-in,
        // and lets AuthGuard rely on isLoading (not an empty roles array) to
        // decide when resolution is genuinely finished.
        setIsLoading(true);
        try {
          const silentRequest: SilentRequest = {
            ...apiRequest,
            account: account,
          };

          const response = await instance.acquireTokenSilent(silentRequest);

          // Roles from ID token claims
          const idClaims = response.idTokenClaims as Record<string, unknown> | undefined;
          const roles = idClaims?.roles ?? [];
          setUserRoles(Array.isArray(roles) ? roles as string[] : [roles as string]);

          // Tenant ID from access token (E1 extension attribute claim)
          // Resolution order:
          //   1. JWT claim (VITE_TENANT_CLAIM_NAME) — authoritative once E1 is configured
          //   2. VITE_CUSTOMER_ID — pre-E1 fallback for all environments
          const resolved = resolveTenantIdFromToken(response.accessToken);
          if (resolved) {
            setTenantId(resolved);
          } else {
            // Pre-E1 fallback: use VITE_CUSTOMER_ID until Entra extension attribute
            // is provisioned on user accounts. Remove this branch after E1 is complete.
            setTenantId((import.meta.env.VITE_CUSTOMER_ID as string | undefined) ?? null);
          }

          setError(null);
        } catch (err) {
          console.error('Failed to extract claims from token:', err);
          setError('Failed to load user permissions');
          setUserRoles([]);
          setTenantId(null);
        }
      } else {
        setUserRoles([]);
        setTenantId(null);
      }
      setIsLoading(false);
    };

    if (inProgress === InteractionStatus.None) {
      extractClaimsFromToken();
    }
  }, [account, isAuthenticated, instance, inProgress]);

  const login = async (loginHint?: string): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      // Use redirect (not popup) — popups are blocked when triggered automatically.
      // loginHint pre-fills the email field on Microsoft's login page.
      await instance.loginRedirect({
        ...loginRequest,
        ...(loginHint ? { loginHint } : {}),
      });
    } catch (err) {
      console.error('Login failed:', err);
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setError(null);
      // Use redirect (not popup) because programmatic logout from session
      // timeout fires outside a user gesture — browsers block popups in that case.
      await instance.logoutRedirect({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
    } catch (err) {
      console.error('Logout failed:', err);
      setError('Logout failed. Please try again.');
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const silentRequest: SilentRequest = {
        ...apiRequest,
        account: account,
      };
      
      const response = await instance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (err) {
      console.error('Failed to acquire access token:', err);
      // Try interactive token acquisition as fallback
      try {
        const response = await instance.acquireTokenPopup(apiRequest);
        return response.accessToken;
      } catch (interactiveErr) {
        console.error('Interactive token acquisition failed:', interactiveErr);
        return null;
      }
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return hasRequiredRole(userRoles, role);
  };

  const contextValue: AuthContextState = {
    isAuthenticated,
    user: account,
    userRoles,
    tenantId,
    isLoading,
    error,
    hasRole,
    login,
    logout,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};